import { httpStatusOf } from "modules/members/api/httpStatus";
import type { MFSelectableResource } from "./catalogs";

export const RECIPIENT_OPTIONS_PATH = "/members/access/recipients/request-options";
export const RECIPIENT_OPTIONS_MEMBER_CAP = 50;
export const RECIPIENT_OPTIONS_ITEM_CAP = 200;
export const RECIPIENT_OPTIONS_CONCURRENCY = 3;
export const BULK_REQUEST_CAP = 200;

export type RecipientOptionsItem = {
  catalogItemId: string;
  selectableResourceId: string;
};

export type MFRecipientItemOptions = RecipientOptionsItem & {
  requestable: boolean;
  selectableResource: MFSelectableResource | null;
};

export type MFRecipientRequestOptions = {
  memberId: string;
  items: MFRecipientItemOptions[];
};

export type RecipientOptionsBody = {
  member_ids: string[];
  items: { catalog_item_id: string; selectable_resource_id: string }[];
};

// (memberId, catalogItemId, selectableResourceId) → the resource as that
// member may request it, or null when they can't request it at all.
export type RecipientOptions = Record<string, MFSelectableResource | null>;

export const recipientOptionsItemKey = ({
  catalogItemId,
  selectableResourceId,
}: RecipientOptionsItem) => `${catalogItemId}__${selectableResourceId}`;

export const recipientOptionKey = (
  memberId: string,
  item: RecipientOptionsItem,
) => `${memberId}__${recipientOptionsItemKey(item)}`;

const chunk = <T>(xs: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
};

// The catalog read already answers for the viewer, so only the other members
// need asking.
export const recipientOptionsMemberIds = (
  memberIds: string[],
  selfMemberId: string | undefined,
): string[] => [...new Set(memberIds)].filter((id) => id !== selfMemberId);

export const recipientOptionsBodies = (
  memberIds: string[],
  items: RecipientOptionsItem[],
): RecipientOptionsBody[] => {
  const pairs = [
    ...new Map(items.map((i) => [recipientOptionsItemKey(i), i])).values(),
  ].map((i) => ({
    catalog_item_id: i.catalogItemId,
    selectable_resource_id: i.selectableResourceId,
  }));
  const itemChunks = chunk(pairs, RECIPIENT_OPTIONS_ITEM_CAP);
  return chunk([...new Set(memberIds)], RECIPIENT_OPTIONS_MEMBER_CAP).flatMap(
    (member_ids) => itemChunks.map((items) => ({ member_ids, items })),
  );
};

// The bulk submit refuses more than BULK_REQUEST_CAP requests, so a basket past
// it is never sent and there is no point asking what it may hold.
export const tooManyToRequest = (
  memberIds: string[],
  items: RecipientOptionsItem[],
): string | null => {
  const count =
    new Set(memberIds).size * new Set(items.map(recipientOptionsItemKey)).size;
  return count > BULK_REQUEST_CAP
    ? `Too many people and resources to request at once (${count}). Request up to ${BULK_REQUEST_CAP} at a time.`
    : null;
};

export const mapWithConcurrency = async <T, R>(
  xs: T[],
  limit: number,
  fn: (x: T) => Promise<R>,
): Promise<R[]> => {
  const out: R[] = new Array(xs.length);
  let next = 0;
  let failed = false;
  const worker = async () => {
    while (!failed && next < xs.length) {
      const i = next++;
      try {
        out[i] = await fn(xs[i]);
      } catch (e) {
        failed = true;
        throw e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, xs.length) }, worker));
  return out;
};

export const indexRecipientOptions = (
  responses: MFRecipientRequestOptions[][],
): RecipientOptions => {
  const index: RecipientOptions = {};
  for (const member of responses.flat())
    for (const item of member.items)
      index[recipientOptionKey(member.memberId, item)] = item.requestable
        ? item.selectableResource
        : null;
  return index;
};

const serverError = (error: unknown) =>
  (error as { error?: unknown } | null)?.error;

// The server refuses the whole read, naming no one, when any member id is one
// the viewer may no longer request for.
export const isRecipientOptionsForbidden = (error: unknown): boolean =>
  httpStatusOf(error) === 403;

export const recipientOptionsError = (error: unknown): string => {
  if (isRecipientOptionsForbidden(error))
    return "You can no longer request access for someone in this request. Check who it's for and try again.";
  const message = serverError(error);
  if (typeof message === "string" && message.trim()) return message;
  return httpStatusOf(error) === 429
    ? "You're checking too many people at once. Wait a minute and try again."
    : "Couldn't check what these people can request. Please try again.";
};
